import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition'
import { RootState } from 'constants/interfaces'
import { getSpeechLanguage } from '../constants'

// The underlying recognizer is shared by every input in the app.
let latestSpeechSession = 0

interface UseSpeechInputProps {
  value: string
  onChange: (value: string) => void
  isOpen: boolean
}

export const useSpeechInput = ({ value, onChange, isOpen }: UseSpeechInputProps) => {
  const locale = useSelector((state: RootState) => state.general.locale)
  const currentSpeechLang = useMemo(() => getSpeechLanguage(locale), [locale])
  const [isStarting, setIsStarting] = useState(false)
  const [ownsRecording, setOwnsRecording] = useState(false)
  const {
    transcript,
    finalTranscript,
    interimTranscript,
    listening,
    browserSupportsSpeechRecognition,
    isMicrophoneAvailable,
    resetTranscript,
  } = useSpeechRecognition()

  const speechSupported = browserSupportsSpeechRecognition
  const acceptSpeechRef = useRef(false)
  const baseTextRef = useRef('')
  const onChangeRef = useRef(onChange)
  const mountedRef = useRef(true)
  const sessionRef = useRef(0)

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const cleanup = useCallback(() => {
    const ownsSession = acceptSpeechRef.current && sessionRef.current === latestSpeechSession
    acceptSpeechRef.current = false
    sessionRef.current = 0
    baseTextRef.current = ''
    if (mountedRef.current) {
      setIsStarting(false)
      setOwnsRecording(false)
    }
    // Recognition is shared globally: an inactive input must not stop another input.
    if (ownsSession) {
      void SpeechRecognition.abortListening().catch(() => {})
    }
  }, [])

  const startListening = useCallback(async () => {
    if (!isOpen || !speechSupported || isMicrophoneAvailable === false || isStarting) return
    const session = ++latestSpeechSession
    sessionRef.current = session
    baseTextRef.current = value.trim() ? value.trimEnd() : ''
    resetTranscript()
    acceptSpeechRef.current = true
    setIsStarting(true)
    setOwnsRecording(true)
    try {
      await SpeechRecognition.startListening({ continuous: true, language: currentSpeechLang })
      // A close/stop can happen while the browser is still starting recognition.
      if ((!mountedRef.current || sessionRef.current !== session) && latestSpeechSession === session) {
        await SpeechRecognition.abortListening()
      }
    } catch (e) {
      if (sessionRef.current === session) acceptSpeechRef.current = false
      console.error('Speech start error', e)
    } finally {
      if (mountedRef.current && sessionRef.current === session) setIsStarting(false)
    }
  }, [currentSpeechLang, value, resetTranscript, speechSupported, isMicrophoneAvailable, isOpen, isStarting])

  const stopListening = useCallback(() => {
    const ownsSession = acceptSpeechRef.current && sessionRef.current === latestSpeechSession
    acceptSpeechRef.current = false
    sessionRef.current = 0
    if (mountedRef.current) {
      setIsStarting(false)
      setOwnsRecording(false)
    }
    if (ownsSession) {
      void SpeechRecognition.stopListening().catch(() => {})
    }
  }, [])

  const handleManualInput = stopListening

  useEffect(() => {
    if (!acceptSpeechRef.current || sessionRef.current !== latestSpeechSession) return
    // transcript includes both final and interim text; interim alone drops earlier words.
    const currentText = transcript.trim()
    if (currentText) {
      onChangeRef.current(baseTextRef.current ? `${baseTextRef.current} ${currentText}` : currentText)
    }
  }, [transcript])

  useEffect(() => {
    if (!isOpen || isMicrophoneAvailable === false) cleanup()
    else if (!isStarting && !listening) {
      acceptSpeechRef.current = false
      setOwnsRecording(false)
    }
  }, [isOpen, isMicrophoneAvailable, isStarting, listening, cleanup])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      cleanup()
    }
  }, [cleanup])

  return {
    isStarting,
    finalTranscript,
    interimTranscript,
    listening:
      isOpen &&
      isMicrophoneAvailable !== false &&
      ownsRecording &&
      sessionRef.current === latestSpeechSession &&
      (isStarting || listening),
    speechSupported,
    isMicrophoneAvailable,
    startListening,
    stopListening,
    handleManualInput,
    cleanup,
  }
}
